import { getStoreFromLoggerContext } from './async-logger-context';
import { writeToConsole } from './console';
import {
    type DebugLogger,
    type DebugLoggerFn,
    type DebugLoggerOptions,
    type DebugLogLabels,
    type DebugLogLevel,
    LOG_LEVELS,
} from './types';

const disabledLoggerNamespaces = new Set<string>();

/**
 * Create a console logger.
 */
export function Logger(namespace = 'default', options: DebugLoggerOptions = {}): DebugLogger {
    const globalLabels = options.labels || {};
    const warning = createLogMethod(namespace, 'warning', globalLabels, options);

    return {
        info: createLogMethod(namespace, 'info', globalLabels, options),
        notice: createLogMethod(namespace, 'notice', globalLabels, options),
        error: createLogMethod(namespace, 'error', globalLabels, options),
        warning,
        warn: warning,
        debug: createLogMethod(namespace, 'debug', globalLabels, options),
        critical: createLogMethod(namespace, 'critical', globalLabels, options),
        emergency: createLogMethod(namespace, 'emergency', globalLabels, options),
        alert: createLogMethod(namespace, 'alert', globalLabels, options),
        setLabels: (labels) => {
            Object.assign(globalLabels, labels);
        },
        child: (suffix: string) => Logger(`${namespace}:${suffix}`, options),
    };
}

/**
 * Disable logging for the given namespaces.
 */
export function disableLogger(...namespaces: string[]) {
    namespaces.forEach((namespace) => disabledLoggerNamespaces.add(namespace));
}

function checkIsLogLevel(level: string): level is DebugLogLevel {
    return LOG_LEVELS.includes(level as DebugLogLevel);
}

function createLogMethod(
    namespace: string,
    level: DebugLogLevel,
    labels: DebugLogLabels,
    options: DebugLoggerOptions
): DebugLoggerFn {
    const enabled = shouldLogCheckAtInit(namespace, level, options.enableForTests);

    // @ts-ignore function is still incomplete
    const log: DebugLoggerFn = enabled
        ? (message: string, ...args: any) => {
              if (!shouldLog(namespace)) {
                  return;
              }
              writeToConsole({
                  namespace,
                  level,
                  message: toMessage(message, options.prefix),
                  args,
                  labels: {
                      ...Object.fromEntries(getStoreFromLoggerContext()?.entries() ?? []),
                      ...labels,
                  },
              });
          }
        : () => {};

    log.stream = {
        write: (message: string) => {
            if (!enabled) {
                return;
            }
            writeToConsole({
                namespace,
                level,
                message: toMessage(message, options.prefix),
                labels,
                args: [],
            });
        },
    };

    return log;
}

function toMessage(message: string, prefix?: string): string {
    if (prefix) {
        return `${prefix} ${message}`;
    }

    return message;
}

function shouldLog(namespace: string) {
    if (disabledLoggerNamespaces.has(namespace)) {
        return false;
    }

    return true;
}

function shouldLogCheckAtInit(namespace: string, level: DebugLogLevel, enableForTests = false) {
    if (disabledLoggerNamespaces.has(namespace)) {
        return false;
    }

    if (process.env.NODE_ENV === 'test' && !enableForTests) {
        return false;
    }

    const logLevel = process.env.GITBOOK_LOG_LEVEL;
    if (logLevel && checkIsLogLevel(logLevel)) {
        return LOG_LEVELS.indexOf(level) >= LOG_LEVELS.indexOf(logLevel);
    }

    if (level === 'debug' && process.env.NODE_ENV !== 'development') {
        const debugEnv = process.env.GITBOOK_DEBUG;
        if (!debugEnv || debugEnv === '*') {
            return false;
        }
    }

    if (LOG_LEVELS.indexOf(level) >= LOG_LEVELS.indexOf('error')) {
        return true;
    }

    if (!process.env.GITBOOK_DEBUG || process.env.GITBOOK_DEBUG === '*') {
        return true;
    }

    return namespace.startsWith(process.env.GITBOOK_DEBUG);
}
